import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  fetchActivity,
  allowSender,
  ApiError,
  type ActivityResponse,
} from "../../api";
import { ActivityRow } from "./ActivityFeed";

interface ActivityFullFeedModalProps {
  onClose: () => void;
  onOpenEmail: (id: string) => void;
}

type FullFeedFilter = "all" | "noise";

/**
 * Full unfiltered activity feed. Includes noise-suppressed events flagged with
 * a NOISE pill. Search by sender/subject substring. Toggle NOISE-only.
 *
 * Inline expansion works the same as the right-rail rows — clicking a row
 * expands the preview, and OPEN FULL EMAIL inside the preview opens the
 * EmailDetailModal on top (the parent ActivityFeed owns email modal state).
 */
export function ActivityFullFeedModal({
  onClose,
  onOpenEmail,
}: ActivityFullFeedModalProps) {
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FullFeedFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Senders rescued this session → show "✓ KEPT" immediately, even before refetch.
  const [rescued, setRescued] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const d = await fetchActivity({ limit: 100, includeNoise: true });
      setData(d);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "fetch_failed",
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRescue = useCallback(
    async (email: string) => {
      const key = email.toLowerCase();
      setRescued((s) => new Set(s).add(key)); // optimistic
      try {
        await allowSender(email);
        await load(); // re-pull so the sender's rows flip to live server-side
      } catch {
        setRescued((s) => {
          const n = new Set(s);
          n.delete(key);
          return n;
        });
      }
    },
    [load],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Client-side search + filter on the already-fetched array.
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.events.filter((e) => {
      if (filter === "noise" && !e.noise) return false;
      if (!q) return true;
      const email = e.email;
      if (!email) return false;
      return (
        email.fromName.toLowerCase().includes(q) ||
        email.fromEmail.toLowerCase().includes(q) ||
        email.subject.toLowerCase().includes(q)
      );
    });
  }, [data, search, filter]);

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card full-feed-modal-card">
        <div className="full-feed-header">
          <div className="write-confirm-title">
            FULL ACTIVITY FEED
            {data && (
              <span className="full-feed-count"> · {data.events.length} events</span>
            )}
          </div>
          <button
            className="email-detail-close"
            onClick={onClose}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        <div className="full-feed-controls">
          <input
            type="text"
            className="full-feed-search"
            placeholder="Search sender or subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="full-feed-filter-group">
            <button
              className={clsx(
                "activity-filter-pill",
                filter === "all" && "active",
              )}
              onClick={() => setFilter("all")}
            >
              ALL
            </button>
            <button
              className={clsx(
                "activity-filter-pill",
                filter === "noise" && "active",
              )}
              onClick={() => setFilter("noise")}
            >
              NOISE
            </button>
          </div>
        </div>

        <div className="full-feed-body">
          {!data && !error && (
            <div className="email-detail-loading">
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-dot" />
              <span className="chat-thinking-label">LOADING FEED</span>
            </div>
          )}
          {error && (
            <div className="write-confirm-error">
              ⚠ Failed to load feed: {error}
            </div>
          )}
          {data && filtered.length === 0 && (
            <div className="activity-empty">
              {search.trim()
                ? `No matches for "${search.trim()}"`
                : "No events to show."}
            </div>
          )}
          {data &&
            filtered.map((evt) => (
              <ActivityRow
                key={evt.id}
                event={evt}
                expanded={expandedId === evt.id}
                onToggle={() =>
                  setExpandedId((cur) => (cur === evt.id ? null : evt.id))
                }
                onOpenEmail={onOpenEmail}
                onRescue={handleRescue}
                rescued={rescued.has((evt.email?.fromEmail ?? "").toLowerCase())}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
